import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Download, Filter, Eye, User, Database, Activity } from 'lucide-react';
import { API_BASE_URL } from '../config/apiConfig';

interface AuditLogEntry {
  audit_id: number;
  table_name: string;
  operation_type: string;
  record_id?: string;
  old_values?: any;
  new_values?: any;
  changed_by: string;
  changed_at: string;
  user_name?: string;
}

interface AuditSummary {
  total_changes: number;
  operations_by_type: Record<string, number>;
  changes_by_user: Record<string, number>;
  changes_by_table: Record<string, number>;
  date_range: {
    start?: string;
    end?: string;
  };
}

interface AuditFilters {
  start_date: string;
  end_date: string;
  table_name: string;
  operation_type: string;
  user_id: string;
  search_term: string;
}

const AuditLogModal: React.FC<{
  entry: AuditLogEntry | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ entry, isOpen, onClose }) => {
  if (!isOpen || !entry) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Audit Log Details</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
              <div className="space-y-2 text-sm">
                <div><strong>Audit ID:</strong> {entry.audit_id}</div>
                <div><strong>Table:</strong> {entry.table_name}</div>
                <div><strong>Operation:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                    entry.operation_type === 'INSERT' ? 'bg-green-100 text-green-800' :
                    entry.operation_type === 'UPDATE' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {entry.operation_type}
                  </span>
                </div>
                <div><strong>Record ID:</strong> {entry.record_id || 'N/A'}</div>
                <div><strong>Changed By:</strong> {entry.user_name || entry.changed_by}</div>
                <div><strong>Timestamp:</strong> {new Date(entry.changed_at).toLocaleString()}</div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Change Details</h4>
              {entry.old_values && (
                <div className="mb-4">
                  <strong className="text-red-600">Old Values:</strong>
                  <pre className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs overflow-auto max-h-32">
                    {JSON.stringify(entry.old_values, null, 2)}
                  </pre>
                </div>
              )}
              {entry.new_values && (
                <div>
                  <strong className="text-green-600">New Values:</strong>
                  <pre className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-xs overflow-auto max-h-32">
                    {JSON.stringify(entry.new_values, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AuditTrail: React.FC = () => {
  const [filters, setFilters] = useState<AuditFilters>({
    start_date: '',
    end_date: '',
    table_name: '',
    operation_type: '',
    user_id: '',
    search_term: ''
  });
  
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [limit] = useState(50);

  // Build query parameters
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) queryParams.append(key, value);
  });
  queryParams.append('limit', limit.toString());
  queryParams.append('offset', (currentPage * limit).toString());

  const { data: auditLogs, isLoading: logsLoading, error } = useQuery<AuditLogEntry[]>({
    queryKey: ['audit-logs', filters, currentPage],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/audit/logs?${queryParams}`);
      if (!response.ok) throw new Error('Failed to fetch audit logs');
      return response.json();
    }
  });

  const { data: summary } = useQuery<AuditSummary>({
    queryKey: ['audit-summary', filters.start_date, filters.end_date],
    queryFn: async () => {
      const summaryParams = new URLSearchParams();
      if (filters.start_date) summaryParams.append('start_date', filters.start_date);
      if (filters.end_date) summaryParams.append('end_date', filters.end_date);
      
      const response = await fetch(`${API_BASE_URL}/audit/summary?${summaryParams}`);
      if (!response.ok) throw new Error('Failed to fetch audit summary');
      return response.json();
    }
  });

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search_term: searchTerm }));
    setCurrentPage(0);
  };

  const handleFilterChange = (key: keyof AuditFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  const handleExport = async () => {
    try {
      const exportParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) exportParams.append(key, value);
      });
      
      const response = await fetch(`${API_BASE_URL}/audit/export/csv?${exportParams}`);
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleViewDetails = (entry: AuditLogEntry) => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-md p-4">
      <p className="text-red-800">Error loading audit trail data</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Audit Trail</h1>
        <button
          onClick={handleExport}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Changes</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.total_changes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <User className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-2xl font-semibold text-gray-900">{Object.keys(summary.changes_by_user).length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Database className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tables Modified</p>
                <p className="text-2xl font-semibold text-gray-900">{Object.keys(summary.changes_by_table).length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Filter className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Most Active Operation</p>
                <p className="text-lg font-semibold text-gray-900">
                  {Object.entries(summary.operations_by_type).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Search</label>
            <div className="relative mt-1">
              <input
                type="text"
                value={filters.search_term}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search users, records, or values..."
                className="block w-full border border-gray-300 rounded-md px-3 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Table</label>
            <select
              value={filters.table_name}
              onChange={(e) => handleFilterChange('table_name', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Tables</option>
              <option value="members">Members</option>
              <option value="activity_records">Activity Records</option>
              <option value="membership_payments">Membership Payments</option>
              <option value="users">Users</option>
              <option value="activity_categories">Activity Categories</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Operation</label>
            <select
              value={filters.operation_type}
              onChange={(e) => handleFilterChange('operation_type', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Operations</option>
              <option value="INSERT">Insert</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({
                  start_date: '',
                  end_date: '',
                  table_name: '',
                  operation_type: '',
                  user_id: '',
                  search_term: ''
                });
                setCurrentPage(0);
              }}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Audit Log Entries</h3>
        </div>

        {logsLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Record ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auditLogs?.map((entry) => (
                    <tr key={entry.audit_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(entry.changed_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.user_name || 'Unknown User'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {entry.changed_by}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          entry.operation_type === 'INSERT' ? 'bg-green-100 text-green-800' :
                          entry.operation_type === 'UPDATE' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {entry.operation_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.table_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.record_id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(entry)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!auditLogs || auditLogs.length < limit}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage + 1}</span> of audit log entries
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!auditLogs || auditLogs.length < limit}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Audit Log Details Modal */}
      <AuditLogModal
        entry={selectedEntry}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedEntry(null);
        }}
      />
    </div>
  );
};